import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Cell,
} from 'recharts';

const FUNNEL_COLORS = [
    CHART_COLORS.chart.line1,
    CHART_COLORS.chart.line2,
    CHART_COLORS.accent,
];

export const AdFunnelChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const funnelData = useMemo(() => {
        const impressions = Number(finance?.metrics?.adImpressions) || 0;
        const clicks = Number(finance?.metrics?.adClicks) || 0;
        const orders = Number(finance?.metrics?.orders) || 0;

        return [
            {
                name: t('charts.impressions', 'Impressions'),
                value: impressions,
                conversionRate: null as number | null,
            },
            {
                name: t('charts.clicks', 'Clicks'),
                value: clicks,
                conversionRate: impressions > 0
                    ? Math.round((clicks / impressions) * 10000) / 100
                    : 0,
            },
            {
                name: t('charts.orders', 'Orders'),
                value: orders,
                conversionRate: clicks > 0
                    ? Math.round((orders / clicks) * 10000) / 100
                    : 0,
            },
        ];
    }, [finance?.metrics, t]);

    const isEmpty = useMemo(
        () => funnelData.every((d) => d.value === 0),
        [funnelData]
    );

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={funnelData}
                        layout="vertical"
                        margin={{ top: 10, right: 40, bottom: 10, left: 80 }}
                    >
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, '')}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={75}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number, _name: string, props: any) => {
                                const payload = props.payload as any;
                                const rate = payload.conversionRate as number | null;
                                const label = rate !== null
                                    ? `${value.toLocaleString()} (${rate}% ${t('charts.conversionRate', 'conv.')})`
                                    : value.toLocaleString();
                                return [label, payload.name as string];
                            }}
                        />
                        <Bar
                            dataKey="value"
                            radius={[0, 3, 3, 0]}
                            maxBarSize={50}
                        >
                            {funnelData.map((_entry, index) => (
                                <Cell key={`cell-${index}`} fill={FUNNEL_COLORS[index]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 px-2 pb-2">
                {funnelData.map((step, index) => (
                    step.conversionRate !== null && (
                        <span
                            key={index}
                            className="text-[10px] text-[var(--text-primary)] opacity-70"
                        >
                            {funnelData[index - 1]?.name} → {step.name}: {step.conversionRate}%
                        </span>
                    )
                ))}
            </div>
        </div>
    );
};
